import { Document, Page, Text, View, StyleSheet, pdf } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontFamily: 'Helvetica',
    backgroundColor: '#FFFFFF',
  },
  header: {
    marginBottom: 30,
    borderBottomWidth: 2,
    borderBottomColor: '#6366F1',
    paddingBottom: 20,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#1F2937',
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 12,
    color: '#6B7280',
  },
  messagesContainer: {
    marginTop: 20,
  },
  messageBlock: {
    marginBottom: 16,
    padding: 12,
    borderRadius: 8,
  },
  userMessage: {
    backgroundColor: '#EEF2FF',
    marginLeft: 40,
  },
  assistantMessage: {
    backgroundColor: '#F9FAFB',
    marginRight: 40,
  },
  messageHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  roleLabel: {
    fontSize: 10,
    fontWeight: 'bold',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  userLabel: {
    color: '#4F46E5',
  },
  assistantLabel: {
    color: '#059669',
  },
  timestamp: {
    fontSize: 9,
    color: '#9CA3AF',
  },
  messageContent: {
    fontSize: 11,
    color: '#374151',
    lineHeight: 1.5,
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 40,
    right: 40,
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
    paddingTop: 15,
  },
  footerText: {
    fontSize: 9,
    color: '#9CA3AF',
    textAlign: 'center',
  },
  pageNumber: {
    fontSize: 9,
    color: '#9CA3AF',
    textAlign: 'center',
    marginTop: 8,
  },
});

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: Date;
}

interface AIChatPDFProps {
  messages: Message[];
  title?: string;
  companyName?: string;
}

const formatTimestamp = (date: Date) => {
  return new Date(date).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const AIChatPDFDocument = ({ messages, title, companyName }: AIChatPDFProps) => (
  <Document>
    <Page size="A4" style={styles.page}>
      <View style={styles.header}>
        <Text style={styles.title}>{title || 'AI Assistant Conversation'}</Text>
        <Text style={styles.subtitle}>
          {companyName ? `${companyName} • ` : ''}
          Generated on {new Date().toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })}
        </Text>
      </View>

      <View style={styles.messagesContainer}>
        {messages.map((message) => (
          <View
            key={message.id}
            style={[
              styles.messageBlock,
              message.role === 'user' ? styles.userMessage : styles.assistantMessage,
            ]}
          >
            <View style={styles.messageHeader}>
              <Text
                style={[
                  styles.roleLabel,
                  message.role === 'user' ? styles.userLabel : styles.assistantLabel,
                ]}
              >
                {message.role === 'user' ? 'You' : 'AI Assistant'}
              </Text>
              <Text style={styles.timestamp}>{formatTimestamp(message.createdAt)}</Text>
            </View>
            <Text style={styles.messageContent}>{message.content}</Text>
          </View>
        ))}
      </View>

      <View style={styles.footer} fixed>
        <Text style={styles.footerText}>
          Exported from SendFeedback AI Assistant
        </Text>
        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
        />
      </View>
    </Page>
  </Document>
);

export async function generateAIChatPDF(
  messages: Message[],
  title?: string,
  companyName?: string
): Promise<Blob> {
  const doc = <AIChatPDFDocument messages={messages} title={title} companyName={companyName} />;
  return await pdf(doc).toBlob();
}

export async function downloadAIChatPDF(
  messages: Message[],
  title?: string,
  companyName?: string
): Promise<void> {
  const blob = await generateAIChatPDF(messages, title, companyName);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `ai-conversation-${new Date().toISOString().split('T')[0]}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export default AIChatPDFDocument;
